import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate, WarningTypeString } from "../../../zod-decorator/index.js";
import type { WarningType } from "../../../WarningDescription.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    WarningTypeNotificationModeFieldsWithDefaults,
    WarningTypeNotificationModeSchemaTypes,
} from "../../types/warningTypeNotificationMode.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import type { NotificationMode } from "../notificationSubscription.js";

const SCHEMA_KEY = "WarningTypeNotificationMode" as const;

/**
 * One entry of a rule's `notificationModes` list: alert on `warningType`, delivered at
 * `notificationMode`. A type with no entry is muted — absence is the only way to mute.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<WarningTypeNotificationMode>>(...addSchemaKeyHook(SCHEMA_KEY))
export class WarningTypeNotificationMode extends SchemaBase<
    WarningTypeNotificationMode,
    WarningTypeNotificationModeFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: WarningTypeString })
    @prop({ type: () => String, required: true })
    public warningType!: WarningType;

    /** How loudly to deliver: `standard` honors quiet hours, `critical` may pierce them. */
    @validate({ zodValidator: z.enum(["standard", "critical"]) })
    @prop({ type: () => String, required: true })
    public notificationMode!: NotificationMode;

    static getters = {} as const satisfies Record<string, (self: WarningTypeNotificationMode) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<WarningTypeNotificationModeSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<WarningTypeNotificationMode, WarningTypeNotificationModeFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

WarningTypeNotificationMode.dtoValidators = getZodSchemaFromClass(
    WarningTypeNotificationMode,
) as unknown as DtoValidatorTypes<WarningTypeNotificationModeSchemaTypes>;
